/** When the composer's auto-parse is worth spending a parse on.
 *
 * A parse costs the same regardless of how much the body grew -- roughly 85%
 * of its prompt is the projects/features/collections context rather than the
 * check-in text -- so the useful question is whether there is enough new prose
 * for the parser to say something it hasn't already said. Firing on every
 * typing pause instead had a single 5,250-character entry parsed 189 times.
 *
 * Callers pair this with a long backstop timer, so a body that never clears
 * the threshold is still parsed once typing stops.
 */

/** Whitespace-insensitive form used for every comparison here, so reflowing a
 *  paragraph or trailing a newline never reads as new content. */
export const normalizeBody = (body: string): string => body.trim().replace(/\s+/g, ' ')

/** True when `next` differs from the last-parsed body by enough to be worth a
 *  parse on the short timer. The first parse of a check-in (no previous body)
 *  always qualifies, so short entries stay as responsive as they ever were.
 *  Deletions count the same as additions: cutting a paragraph changes what
 *  should be extracted as much as adding one. */
export const hasSubstantialNewText = (
    lastParsedBody: string | undefined,
    next: string,
    minNewChars: number,
): boolean => {
    if (lastParsedBody === undefined) return true
    return Math.abs(normalizeBody(next).length - lastParsedBody.length) >= minNewChars
}

/** True when a parse would tell us nothing: the body is byte-identical, once
 *  normalized, to what the parser last saw. */
export const isAlreadyParsed = (lastParsedBody: string | undefined, next: string): boolean =>
    lastParsedBody !== undefined && lastParsedBody === normalizeBody(next)
